import { useState } from 'react';
import { BUNDLES, PRICING_TIERS, type BundleId, type PlanType } from '../../shared/types';
import { useAuth } from '../contexts/AuthContext';
import { createCheckoutSession, createBundleCheckout } from '../services/api';

// Replace with the real cal.com booking URL when ready.
const CAL_COM_URL = 'https://cal.com/ludovicmornand/evinly-prise-de-contact';
// Served from public/videos/ (Express caches static files for 1 year):
// to replace the video, publish it under a NEW file name, never overwrite.
const DEMO_VIDEO_URL = '/videos/evinly-onboarding.mp4';
const DEMO_VIDEO_POSTER = '/videos/evinly-onboarding-poster.jpg';

interface OnboardingScreenProps {
  /** Called when the user wants to see the full pricing page (more details, comparison). */
  onViewFullPricing: () => void;
}

export default function OnboardingScreen({ onViewFullPricing }: OnboardingScreenProps) {
  const { user } = useAuth();
  const firstName = user?.displayName?.split(' ')[0] ?? user?.email?.split('@')[0] ?? '';

  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [interval, setInterval] = useState<'month' | 'year'>('month');

  // Prix abos sourcés de la source de vérité (shared/types) plutôt qu'en dur,
  // pour que le toggle Mensuel/Annuel reste cohérent avec la page Forfaits.
  const proTier = PRICING_TIERS.find((t) => t.plan === 'pro')!;
  const premiumTier = PRICING_TIERS.find((t) => t.plan === 'premium')!;
  const proPriceCents = (interval === 'year' ? proTier.priceYearly : proTier.priceMonthly) ?? 0;
  const premiumPriceCents = (interval === 'year' ? premiumTier.priceYearly : premiumTier.priceMonthly) ?? 0;

  async function handleSubscribe(plan: PlanType) {
    if (!user) return;
    setError(null);
    setLoadingAction(plan);
    try {
      const { url } = await createCheckoutSession(plan as 'pro' | 'premium', interval);
      if (!url) throw new Error('URL de paiement manquante.');
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du paiement.');
      setLoadingAction(null);
    }
  }

  async function handleBundle(bundleId: BundleId) {
    if (!user) return;
    setError(null);
    setLoadingAction(`bundle:${bundleId}`);
    try {
      const { url } = await createBundleCheckout(bundleId);
      if (!url) throw new Error('URL de paiement manquante.');
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du paiement.');
      setLoadingAction(null);
    }
  }

  const packTest = BUNDLES.find((b) => b.id === 'pack_test');
  const packLancement = BUNDLES.find((b) => b.id === 'pack_lancement');
  const otherBundles = BUNDLES.filter((b) => b.id !== 'pack_test' && b.id !== 'pack_lancement');

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 animate-fade-in">
      {/* Welcome */}
      <div className="text-center mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-normal mb-3 text-slate-900 dark:text-white shader:text-white">
          Bienvenue{firstName ? `, ${firstName}` : ''}.
        </h1>
        <p className="text-slate-600 dark:text-slate-400 shader:text-white/60 max-w-xl mx-auto">
          Une dernière étape avant de pouvoir analyser votre première création :
          choisir comment vous souhaitez utiliser Evinly.
        </p>
        <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-50 dark:bg-violet-900/30 shader:bg-violet-500/10 border border-violet-200 dark:border-violet-700/50 shader:border-violet-400/30 text-xs font-medium text-violet-700 dark:text-violet-300 shader:text-violet-200">
          <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
          Version bêta — limitée à 100 places
        </div>
      </div>

      {/* Demo video — pleine largeur, alignée aux blocs abonnement/bundle */}
      <div className="mb-10">
        <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 shader:border-white/10 bg-slate-900 aspect-video flex items-center justify-center">
          {DEMO_VIDEO_URL ? (
            <video
              src={DEMO_VIDEO_URL}
              controls
              playsInline
              preload="metadata"
              poster={DEMO_VIDEO_POSTER}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="text-center px-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4">
                <svg className="w-7 h-7 text-white/80 ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <p className="text-white/80 text-sm">
                Démo vidéo (90 secondes) — bientôt disponible
              </p>
              <p className="text-white/40 text-xs mt-1">
                Voyez Evinly analyser un slogan en temps réel
              </p>
            </div>
          )}
        </div>
        <p className="text-xs text-center text-slate-400 dark:text-slate-500 shader:text-white/40 mt-2">
          3 minutes pour voir Evinly à l'œuvre sur une bière, un vin et un spiritueux
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="max-w-2xl mx-auto mb-6 p-3 rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Two paths */}
      <div className="grid md:grid-cols-2 gap-5 mb-8">
        {/* Path 1 — Subscription */}
        <div className="rounded-2xl p-6 bg-indigo-600 text-white relative overflow-hidden">
          <div className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wide bg-amber-400 text-amber-900 px-2 py-0.5 rounded">
            Usage régulier
          </div>
          <h2 className="text-xl font-semibold mb-1">Abonnement</h2>
          <p className="text-sm text-indigo-200 mb-4">
            Analyses illimitées, rentable dès la 2e analyse par mois.
          </p>

          {/* Toggle facturation — n'affecte que les abonnements (les bundles sont one-shot) */}
          <div className="flex items-center gap-2 mb-5">
            <button
              type="button"
              onClick={() => setInterval('month')}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                interval === 'month'
                  ? 'bg-white text-indigo-700 font-medium'
                  : 'bg-white/10 text-indigo-200 hover:bg-white/15'
              }`}
            >
              Mensuel
            </button>
            <button
              type="button"
              onClick={() => setInterval('year')}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                interval === 'year'
                  ? 'bg-white text-indigo-700 font-medium'
                  : 'bg-white/10 text-indigo-200 hover:bg-white/15'
              }`}
            >
              Annuel
              <span className="ml-1 text-emerald-300 font-semibold">-20%</span>
            </button>
          </div>

          <div className="space-y-3 mb-6">
            <button
              onClick={() => handleSubscribe('pro')}
              disabled={loadingAction !== null}
              className={`w-full text-left p-4 rounded-lg bg-white/10 hover:bg-white/15 transition-colors border border-white/20 ${
                loadingAction === 'pro' ? 'cursor-wait opacity-70' : ''
              } ${loadingAction !== null && loadingAction !== 'pro' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-baseline justify-between mb-0.5">
                <span className="font-semibold">Pro</span>
                <span className="text-lg font-bold">{(proPriceCents / 100).toFixed(0)}€<span className="text-sm font-normal text-indigo-200">/mois</span></span>
              </div>
              <p className="text-xs text-indigo-200">
                Analyses texte + étiquettes illimitées · historique · export PDF
              </p>
            </button>

            <button
              onClick={() => handleSubscribe('premium')}
              disabled={loadingAction !== null}
              className={`w-full text-left p-4 rounded-lg bg-white/10 hover:bg-white/15 transition-colors border border-white/20 ${
                loadingAction === 'premium' ? 'cursor-wait opacity-70' : ''
              } ${loadingAction !== null && loadingAction !== 'premium' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-baseline justify-between mb-0.5">
                <span className="font-semibold">Premium</span>
                <span className="text-lg font-bold">{(premiumPriceCents / 100).toFixed(0)}€<span className="text-sm font-normal text-indigo-200">/mois</span></span>
              </div>
              <p className="text-xs text-indigo-200">
                Tout le Pro + analyses social, campagnes, événement · API · 3 utilisateurs
              </p>
            </button>
          </div>

          <p className="text-xs text-indigo-200 flex items-start gap-1.5">
            <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>Garantie satisfait ou remboursé 30 jours, sans question.</span>
          </p>
        </div>

        {/* Path 2 — Bundles */}
        <div className="rounded-2xl p-6 bg-white dark:bg-slate-900 shader:bg-white/5 border border-slate-200 dark:border-slate-800 shader:border-white/10 relative">
          <div className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wide bg-slate-200 dark:bg-slate-700 shader:bg-white/10 text-slate-700 dark:text-slate-300 shader:text-white/70 px-2 py-0.5 rounded">
            Projet ponctuel
          </div>
          <h2 className="text-xl font-semibold mb-1 text-slate-900 dark:text-white shader:text-white">
            Bundles
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 shader:text-white/60 mb-5">
            Achetez le nombre exact d'analyses dont vous avez besoin, sans engagement.
          </p>

          {packTest && (
            <button
              onClick={() => handleBundle(packTest.id)}
              disabled={loadingAction !== null}
              className={`w-full text-left p-4 rounded-lg border border-indigo-300 dark:border-indigo-700 shader:border-indigo-400/30 bg-indigo-50 dark:bg-indigo-950/20 shader:bg-indigo-500/5 hover:border-indigo-500 transition-colors mb-3 ${
                loadingAction === `bundle:${packTest.id}` ? 'cursor-wait opacity-70' : ''
              } ${loadingAction !== null && loadingAction !== `bundle:${packTest.id}` ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-semibold text-slate-900 dark:text-white shader:text-white">
                  {packTest.name}
                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-indigo-500 text-white px-1.5 py-0.5 rounded">
                    Conseillé
                  </span>
                </span>
                <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400 shader:text-indigo-300">
                  {(packTest.priceCents / 100).toFixed(0)}€
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 shader:text-white/60">
                {packTest.creditLines[0]?.label}
              </p>
            </button>
          )}

          <div className="space-y-2 mb-4">
            {packLancement && (
              <button
                onClick={() => handleBundle(packLancement.id)}
                disabled={loadingAction !== null}
                className={`w-full text-left p-3 rounded-lg border border-slate-200 dark:border-slate-700 shader:border-white/10 hover:border-indigo-400 transition-colors ${
                  loadingAction === `bundle:${packLancement.id}` ? 'cursor-wait opacity-70' : ''
                } ${loadingAction !== null && loadingAction !== `bundle:${packLancement.id}` ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-medium text-slate-900 dark:text-white shader:text-white">
                    {packLancement.name}
                  </span>
                  <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400 shader:text-indigo-300">
                    {(packLancement.priceCents / 100).toFixed(0)}€
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 shader:text-white/50 mt-0.5">
                  {packLancement.totalCredits} analyses pour un lancement complet
                </p>
              </button>
            )}
          </div>

          <button
            onClick={onViewFullPricing}
            className="text-xs text-indigo-600 dark:text-indigo-400 shader:text-indigo-300 hover:underline"
          >
            Voir tous les bundles ({otherBundles.length} autres options) →
          </button>

          <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/50 flex items-start gap-1.5 mt-4 pt-4 border-t border-slate-200 dark:border-slate-800 shader:border-white/10">
            <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>Bundle non utilisé : remboursé intégralement sous 7 jours.</span>
          </p>
        </div>
      </div>

      {/* Reassurance row */}
      <div className="grid md:grid-cols-3 gap-4 mb-8 text-sm">
        <div className="flex items-start gap-2.5 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 shader:bg-white/5">
          <svg className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m0 0v3m0-3h3m-3 0H9m12-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium text-slate-900 dark:text-white shader:text-white">Vos données privées</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/50 mt-0.5">
              Hébergées en UE, jamais réutilisées pour entraîner d'autres modèles.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2.5 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 shader:bg-white/5">
          <svg className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          <div>
            <p className="font-medium text-slate-900 dark:text-white shader:text-white">Annulation en 1 clic</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/50 mt-0.5">
              Depuis votre espace Stripe, sans appel ni mail à envoyer.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2.5 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 shader:bg-white/5">
          <svg className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div>
            <p className="font-medium text-slate-900 dark:text-white shader:text-white">Verdict en 2 minutes environ</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/50 mt-0.5">
              Pipeline 3 juges + sources Légifrance pour chaque analyse.
            </p>
          </div>
        </div>
      </div>

      {/* Founder-led demo */}
      <div className="rounded-2xl p-6 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/30 dark:to-indigo-950/30 shader:from-violet-500/10 shader:to-indigo-500/10 border border-violet-200 dark:border-violet-900/50 shader:border-violet-400/20 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white dark:bg-slate-900 shader:bg-white/10 mb-3">
          <svg className="w-6 h-6 text-violet-600 dark:text-violet-400 shader:text-violet-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold mb-1 text-slate-900 dark:text-white shader:text-white">
          Pas sûr de quoi choisir pour votre cas ?
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 shader:text-white/60 mb-4 max-w-md mx-auto">
          15 minutes avec moi pour voir ensemble si Evinly colle à votre besoin
          et identifier le bon parcours.
        </p>
        <a
          href={CAL_COM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors"
        >
          Réserver 15 min
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </a>
      </div>
    </div>
  );
}
